import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const stats = [
  { value: '50+', label: 'WEB制作実績' },
  { value: '90%', label: 'リピート・紹介率' },
  { value: '全国', label: '対応エリア' },
];

export default function Hero() {
  const headRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 120);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0E1A12]"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20forest%20night%20landscape%20with%20misty%20light%20rays%20breaking%20through%20towering%20trees%2C%20deep%20emerald%20and%20black%20tones%2C%20minimalist%20Japanese%20zen%20aesthetic%2C%20dramatic%20volumetric%20lighting%2C%20cinematic%20ultra%20wide%20angle%2C%20highly%20stylized%20artistic%20photography%2C%20no%20people%2C%20moody%20serene%20atmosphere%2C%20premium%20editorial%20quality&width=1920&height=1080&seq=grainn-hero-v2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E1A12]/90 via-[#0E1A12]/65 to-[#0E1A12]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1A12] via-transparent to-[#0E1A12]/40" />
      </div>

      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(#C9A878 1px, transparent 1px), linear-gradient(90deg, #C9A878 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Floating accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#C9A878]/30 to-transparent" />

      {/* Content */}
      <div
        ref={headRef}
        className={`relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-28 transition-all duration-1000 ease-out ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: Main Copy */}
          <div className="lg:col-span-7">
            <div
              className={`transition-all duration-700 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-[#C9A878] text-xs tracking-[0.45em] mb-7 uppercase flex items-center gap-3">
                <span className="inline-block w-6 h-px bg-[#C9A878]" />
                Web Design &amp; Digital Marketing — Osaka
              </p>
            </div>

            <div
              className={`transition-all duration-700 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <h1 className="text-white font-bold leading-[1.08] mb-8 tracking-tight">
                <span className="block text-4xl md:text-6xl lg:text-7xl mb-1">
                  デザインだけじゃない。
                </span>
                <span className="block text-4xl md:text-6xl lg:text-7xl text-[#C9A878]">
                  成果を生む
                </span>
                <span className="block text-4xl md:text-6xl lg:text-7xl">
                  WEB戦略を。
                </span>
              </h1>
            </div>

            <div
              className={`w-16 h-px bg-[#C9A878]/50 mb-7 transition-all duration-700 delay-300 ${
                visible ? 'opacity-100' : 'opacity-0'
              }`}
            />

            <div
              className={`transition-all duration-700 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-10 font-light max-w-lg">
                WEBサイト制作・SNS運用代行・MEO対策——<br />
                ひとりの担当が一気通貫で伴走します。<br />
                大阪を拠点に、全国対応可能です。
              </p>
            </div>

            <div
              className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-[400ms] ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <Link
                to="/contact"
                className="bg-[#C9A878] text-[#1C2B22] px-10 py-5 rounded-full text-sm tracking-widest font-bold hover:bg-[#B8916A] transition-all duration-300 whitespace-nowrap text-center"
              >
                無料相談を申し込む
              </Link>
              <Link
                to="/services"
                className="border border-white/40 text-white px-10 py-5 rounded-full text-sm tracking-widest hover:bg-white/10 transition-all duration-300 whitespace-nowrap text-center"
              >
                サービスを見る
              </Link>
            </div>
          </div>

          {/* Right: Stats Cards */}
          <div
            className={`lg:col-span-5 grid grid-cols-2 lg:grid-cols-1 gap-4 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            {stats.map((s) => (
              <div
                key={s.label}
                className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-6 hover:bg-white/[0.10] transition-colors"
              >
                <p className="text-3xl md:text-4xl font-bold text-white mb-1 tracking-tight">
                  {s.value}
                </p>
                <p className="text-white/50 text-xs tracking-widest">{s.label}</p>
              </div>
            ))}

            {/* Trust badge */}
            <div className="col-span-2 lg:col-span-1 bg-[#C9A878]/10 border border-[#C9A878]/30 rounded-2xl px-6 py-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#C9A878]/20">
                  <i className="ri-shield-check-line text-[#C9A878] text-sm" />
                </div>
                <p className="text-[#C9A878] text-xs tracking-widest font-medium">初回相談 無料</p>
              </div>
              <p className="text-white/50 text-xs leading-relaxed">
                費用・スケジュール・ご要望など、<br />気軽にご相談ください。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 transition-all duration-700 delay-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <span className="text-white/30 text-[10px] tracking-[0.3em]">SCROLL</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/30 to-transparent relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full bg-white/70 rounded-full"
            style={{
              height: '40%',
              animation: 'scrollDrop 1.8s ease-in-out infinite',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes scrollDrop {
          0%   { transform: translateY(-100%); opacity: 0; }
          30%  { opacity: 1; }
          100% { transform: translateY(300%); opacity: 0; }
        }
      `}</style>
    </section>
  );
}
